import React, { useState, useCallback, useEffect, useRef } from 'react';
import { IonButton, IonIcon, IonSpinner } from '@ionic/react';
import { playOutline, refreshOutline } from 'ionicons/icons';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useCodeEditorSettings } from '../hooks/useSettings';
import { soundService } from '../services/SoundService';
import { hapticService } from '../services/HapticService';
import './CodeEditor.css';

/**
 * CodeEditor Component
 * Interactive code editor with syntax highlighting and execution
 */
function CodeEditor({
  code = '',
  language = 'python',
  onChange,
  onRun,
  editable = true,
  runnable = true,
  showLineNumbers: showLineNumbersProp,
  placeholder = '# Write your code here...',
  height = 200,
  running = false
}) {
  const [localCode, setLocalCode] = useState(code);
  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  
  // Get settings from user preferences
  const { fontSize, showLineNumbers: settingsShowLineNumbers } = useCodeEditorSettings();
  
  // Use prop if explicitly set, otherwise use settings
  const showLineNumbers = showLineNumbersProp !== undefined ? showLineNumbersProp : settingsShowLineNumbers;

  // Calculate line count for line numbers
  const lineCount = localCode ? localCode.split('\n').length : 1;
  const lineNumbers = Array.from({ length: Math.max(lineCount, 1) }, (_, i) => i + 1);

  // Sync scroll between textarea and line numbers
  const handleScroll = useCallback((e) => {
    if (lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = e.target.scrollTop;
    }
  }, []);

  // Sync localCode when parent's code prop changes (e.g., template loaded)
  useEffect(() => {
    setLocalCode(code);
  }, [code]);

  const handleChange = useCallback((e) => {
    const newCode = e.target.value;
    setLocalCode(newCode);
    if (onChange) {
      onChange(newCode);
    }
  }, [onChange]);

  const handleRun = useCallback(() => {
    if (onRun && !running) {
      // Play click sound and haptic when Run button is pressed
      soundService.playClick();
      hapticService.light();
      onRun(localCode);
    }
  }, [onRun, localCode, running]);

  const handleReset = useCallback(() => {
    soundService.playClick();
    hapticService.light();
    setLocalCode(code);
    if (onChange) {
      onChange(code);
    }
  }, [code, onChange]);

  const handleKeyDown = useCallback((e) => {
    // Ctrl/Cmd + Enter to run
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleRun();
    }
    // Tab for indentation
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      const newCode = localCode.substring(0, start) + '    ' + localCode.substring(end);
      setLocalCode(newCode);
      if (onChange) onChange(newCode);
      // Move cursor after tab
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = start + 4;
      }, 0);
    }
  }, [localCode, onChange, handleRun]);

  const getLanguageForHighlighter = () => {
    const langMap = {
      'python': 'python',
      'javascript': 'javascript',
      'java': 'java',
      'cpp': 'cpp',
      'c': 'c'
    };
    return langMap[language] || 'python';
  };

  return (
    <div className="code-editor-container">
      <div className="code-editor-header">
        <span className="language-badge">{language.toUpperCase()}</span>
        <div className="editor-actions">
          {editable && localCode !== code && (
            <IonButton 
              fill="clear" 
              size="small" 
              onClick={handleReset}
              className="reset-btn"
            >
              <IonIcon icon={refreshOutline} slot="icon-only" />
            </IonButton>
          )}
          {runnable && (
            <IonButton 
              fill="solid" 
              size="small" 
              onClick={handleRun}
              disabled={running}
              className="run-btn"
            >
              {running ? (
                <IonSpinner name="crescent" />
              ) : (
                <>
                  <IonIcon icon={playOutline} slot="start" />
                  Run
                </>
              )}
            </IonButton>
          )}
        </div>
      </div>

      <div className="code-editor-body" style={{ minHeight: height }}>
        {editable ? (
          <div className="code-editor-wrapper">
            {showLineNumbers && (
              <div 
                className="line-numbers-gutter"
                ref={lineNumbersRef}
                style={{ fontSize: `${fontSize}px` }}
              >
                {lineNumbers.map(num => (
                  <div key={num} className="line-number">{num}</div>
                ))}
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={localCode}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onScroll={handleScroll}
              placeholder={placeholder}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              className={`code-textarea ${showLineNumbers ? 'with-line-numbers' : ''}`}
              style={{ 
                minHeight: height,
                fontSize: `${fontSize}px`
              }}
            />
          </div>
        ) : (
          <SyntaxHighlighter
            language={getLanguageForHighlighter()}
            style={tomorrow}
            showLineNumbers={showLineNumbers}
            customStyle={{
              margin: 0,
              borderRadius: '0 0 8px 8px',
              minHeight: height,
              fontSize: `${fontSize}px`
            }}
          >
            {localCode || placeholder}
          </SyntaxHighlighter>
        )}
      </div>

      {runnable && (
        <div className="code-editor-hint">
          Press <kbd>Ctrl</kbd> + <kbd>Enter</kbd> to run
        </div>
      )}
    </div>
  );
}

export default CodeEditor;